import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import api from '../../utils/axiosConfig';
import { login, logout } from '../../store/authSlice';
import { authStorage, sessionStore } from '../../platform/storage';
import { clearAccountContactCache } from '../../platform/accountCleanup';
import { useAccountDeletionAvailability } from '../../hooks/useAccountDeletionAvailability';

const receiptKey = 'accountDeletionReceipt';
export const BillingNotice = () => <Alert severity="warning">
    <strong>This does not cancel service or stop billing.</strong><br />
    Removing caregiver access or deleting your account does not cancel any customer’s service.
    Any existing charges continue under the service provider’s agreement, whether billed to you,
    the customer, or someone else. Contact the service provider directly to discuss billing or cancel service.
</Alert>;

export default function AccountDeletion() {
    const auth = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const identity = `${auth.user?.user ?? ''}:${auth.activeUnit?.CustomerKey ?? ''}`;
    const current = useRef({ auth, identity });
    current.current = { auth, identity };
    const mounted = useRef(true);
    const reviewHeading = useRef(null);
    const available = useAccountDeletionAvailability();
    const [preview, setPreview] = useState(null);
    const [password, setPassword] = useState('');
    const [acknowledged, setAcknowledged] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [pending, setPending] = useState(null);
    const [result, setResult] = useState(null);
    const [localCleanup, setLocalCleanup] = useState(false);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        if (available !== true) return;
        let active = true;
        sessionStore.get(receiptKey).then(saved => { if (active && saved) setPending(saved); });
        return () => { active = false; };
    }, [available]);

    useEffect(() => {
        setPreview(null); setPassword(''); setAcknowledged(false);
    }, [identity]);
    useEffect(() => { if (preview) reviewHeading.current?.focus(); }, [preview]);

    async function clearDeletedAccount() {
        const outcomes = await Promise.allSettled([
            clearAccountContactCache(), dispatch(logout({ explicitDeviceLogout: true }))
        ]);
        const failure = outcomes.find(outcome => outcome.status === 'rejected');
        if (failure) throw failure.reason;
    }

    async function finish(saved, outcome) {
        // Do not sign out a different account that signed in while the request ran.
        const latest = current.current.auth;
        if (saved.userKey && (!latest.user?.user || String(latest.user.user) === String(saved.userKey))) {
            if (outcome.accountDeleted) {
                setLocalCleanup(true);
                await clearDeletedAccount();
                setLocalCleanup(false);
            } else if (latest.isAuthenticated) {
                const units = latest.units.filter(u => String(u.CustomerKey) !== String(saved.customerKey));
                dispatch(login({ ...latest, units, activeUnit: units[0] || null }));
                // Persist removal before navigating or allowing a reload.
                await Promise.all([authStorage.set('units', units), authStorage.set('activeUnit', units[0] || null)]);
                await clearAccountContactCache();
            }
        }
        await sessionStore.remove('lastNonProfileRoute');
        // Keep only an opaque receipt for follow-up; discard identity after cleanup.
        await sessionStore.set(receiptKey, { receipt: saved.receipt });
        if (mounted.current) { setPending({ receipt: saved.receipt }); setResult(outcome); setPreview(null); }
    }

    async function checkStatus(saved = pending) {
        setBusy(true); setError('');
        try {
            const { data } = await api.post('/api/account/deletion/status', { receipt: saved.receipt }, { headers: { Authorization: '' } });
            if (!mounted.current) return;
            if (data.recorded) await finish(saved, data);
            else setError('No completed removal is recorded yet. If your request just timed out, check again before starting a new request.');
        } catch { if (mounted.current) setError('Unable to check the result. Keep this page and try checking again.'); }
        finally { if (mounted.current) setBusy(false); }
    }

    async function review(action) {
        const scope = current.current.identity;
        setBusy(true); setError(''); setPassword(''); setAcknowledged(false);
        try {
            const { data } = await api.post('/api/account/deletion/preview', { action, customerKey: auth.activeUnit?.CustomerKey },
                { sessionToken: auth.token });
            if (mounted.current && current.current.identity === scope) setPreview(data);
        } catch (failure) {
            if (mounted.current && current.current.identity === scope) setError(failure.response?.data?.message || 'Unable to load your account. Try again.');
        } finally { if (mounted.current) setBusy(false); }
    }

    async function confirm(event) {
        event.preventDefault();
        if (!preview || !acknowledged || !password || busy) return;
        const saved = { receipt: preview.receipt, userKey: auth.user?.user, customerKey: auth.activeUnit?.CustomerKey };
        const scope = current.current.identity;
        setBusy(true); setError('');
        let sent = false;
        try {
            // Retain an opaque recovery receipt BEFORE the irreversible request.
            // Native storage failure must not fall back to plaintext.
            await sessionStore.set(receiptKey, saved);
            if (current.current.identity !== scope || !mounted.current) {
                await sessionStore.remove(receiptKey);
                return;
            }
            setPending(saved);
            sent = true;
            const { data } = await api.post('/api/account/deletion/confirm', {
                confirmation: preview.confirmation, password, receipt: preview.receipt
            }, { sessionToken: auth.token });
            setPassword('');
            if (mounted.current) await finish(saved, { ...data, recorded: true, cleanup: 'pending' });
        } catch (failure) {
            setPassword('');
            if (!mounted.current) return;
            if (!sent) {
                setError('Unable to save the confirmation securely. No removal request was sent. Please try again.');
            } else if ([400, 403, 409, 422, 429].includes(failure.response?.status)) {
                await sessionStore.remove(receiptKey); setPending(null);
                if (failure.response.status === 409) setPreview(null);
                setError(failure.response.data?.message || 'Review your account and try again.');
            } else setError('The result could not be confirmed. Check the request status before trying again.');
        } finally { if (mounted.current) setBusy(false); }
    }

    async function retryLocalCleanup() {
        setBusy(true); setError('');
        try {
            await clearDeletedAccount();
            setLocalCleanup(false);
            await checkStatus();
        } catch { setError('Unable to clear saved sign-in details on this device. Please retry.'); }
        finally { setBusy(false); }
    }

    if (available !== true) return <Paper sx={{ p: 3, maxWidth: 720, mx: 'auto', my: 3 }}>
        {available === null ? <CircularProgress aria-label="Checking account deletion availability" /> :
            <Alert severity="info">Self-service removal is not available in this environment yet. Contact your service provider for help.</Alert>}
        <Button component={Link} to={auth.isAuthenticated ? '/profile' : '/login'}>
            {auth.isAuthenticated ? 'Back to Profile' : 'Back to sign in'}
        </Button>
    </Paper>;

    const customerCount = new Set(auth.units.map(u => String(u.CustomerKey))).size;
    return <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720, mx: 'auto', my: 3 }}>
        <Stack spacing={2}>
            <Button component={Link} to={auth.isAuthenticated ? '/profile' : '/login'}
                startIcon={<ArrowBackIcon />} disabled={busy || localCleanup}
                sx={{ alignSelf: 'flex-start', minHeight: 44 }}>
                {auth.isAuthenticated ? 'Back to Profile' : 'Back to sign in'}
            </Button>
            <Typography component="h1" variant="h5">Account access &amp; deletion</Typography>
            <Typography>Leave a customer’s caregiver list or permanently delete your Connect App 365 account.
                Deleting your account removes your access to every customer. It does not delete customers or other caregivers.</Typography>
            <BillingNotice />
            {error && <Alert severity="error">{error}</Alert>}
            {localCleanup ? <>
                <Alert severity="warning">Account access was removed. Saved sign-in details still need to be cleared from this device.</Alert>
                <Button disabled={busy} onClick={retryLocalCleanup}>Retry device cleanup</Button>
            </> : result ? <>
                <Alert severity="success">{result.accountDeleted ? 'Your app account has been deleted.' : 'Your caregiver access to this customer has been removed. Your other assignments remain available.'}</Alert>
                <Typography>A confirmation email is queued. Device contact-list updates and provider notices are processed separately.</Typography>
                <Typography role="status">{result.cleanup === 'complete' ? 'Follow-up processing is complete.' : result.cleanup === 'needs_attention'
                    ? 'Some follow-up processing needs support attention. Your access has already been removed.' : 'Follow-up processing is pending.'}</Typography>
                <Button disabled={busy} onClick={() => checkStatus()}>Refresh processing status</Button>
                <Button component={Link} to={auth.isAuthenticated ? '/dashboard' : '/login'}>{auth.isAuthenticated ? 'Return to Dashboard' : 'Return to sign in'}</Button>
                
            </> : pending ? <>
                <Typography>A removal request is awaiting confirmation. Check its status before submitting another request.</Typography>
                <Button variant="contained" disabled={busy} onClick={() => checkStatus()}>Check request status</Button>
                {auth.isAuthenticated && <Button disabled={busy} onClick={async () => {
                    await sessionStore.remove(receiptKey); setPending(null); setPreview(null); setError('');
                }}>Review account again</Button>}
            </> : !auth.isAuthenticated ? <>
                <Typography>Sign in, open Profile, then choose Account access &amp; deletion. You can complete deletion on this website without installing the app.
                    You will review the affected customers and confirm with your current password.</Typography>
                <Button component={Link} to="/login" variant="contained">Sign in</Button>
                <Button component={Link} to="/forgotPassword">Reset your password</Button>
            </> : preview ? <Box component="form" onSubmit={confirm}>
                    <Stack spacing={2}>
                        <Typography component="h2" variant="h6" tabIndex={-1} ref={reviewHeading}>{preview.accountDeleted ? 'Permanently delete your app account?' : 'Leave this customer?'}</Typography>
                        <Typography>You will lose access and stop receiving new alerts for:</Typography>
                        <Box component="ul" sx={{ pl: 3, m: 0 }}>{preview.customers.map(c => <li key={c.customerKey}>{c.name}</li>)}</Box>
                        {preview.accountDeleted && <Typography>Your profile, saved preferences, sign-in credentials and assignments will be deleted. This cannot be undone.
                            Temporary details needed for confirmation email and device-list cleanup are removed after processing.</Typography>}
                        <TextField label="Current password" type="password" autoComplete="current-password" required
                            value={password} onChange={e => setPassword(e.target.value)} disabled={busy} fullWidth />
                        <Button component={Link} to="/forgotPassword" disabled={busy}>Forgot password?</Button>
                        <FormControlLabel control={<Checkbox checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} disabled={busy} />}
                            label="I understand this removes app access and does not cancel service or stop billing." />
                        <Button color="error" variant="contained" type="submit" disabled={busy || !password || !acknowledged}>
                            {busy ? 'Processing…' : preview.accountDeleted ? 'Delete my app account' : 'Leave this customer'}</Button>
                        <Button disabled={busy} onClick={() => { setPreview(null); setPassword(''); }}>Cancel</Button>
                    </Stack>
                </Box> : <>
                    {customerCount > 1 && auth.activeUnit?.CustomerKey && <Button variant="outlined" disabled={busy} onClick={() => review('leave')}>Leave this customer</Button>}
                    <Button color="error" variant="outlined" disabled={busy} onClick={() => review('delete')}>Delete my app account</Button>
                </>}
        </Stack>
    </Paper>;
}
